import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, IconButton, Theme, Typography, useTheme } from "@mui/material";

export type CustomAlertSeverity = "info" | "warning" | "error";

type AlertColors = {
    bg: string;
    border: string;
    iconColor: string;
};

const severityToIcon: Record<CustomAlertSeverity, string> = {
    info: "fa-circle-info",
    warning: "fa-triangle-exclamation",
    error: "fa-circle-exclamation",
};

type CustomAlertProps = {
    severity: CustomAlertSeverity;
    title?: React.ReactNode;
    onClose?: () => void;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
};

const getAlertColors = (theme: Theme, severity: CustomAlertSeverity): AlertColors => theme.palette.emteria.alerts[severity];

const useStyles = makeStyles<{ severity: CustomAlertSeverity }>()((theme, { severity }) => {
    const colors = getAlertColors(theme, severity);
    return {
        root: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            borderRadius: 10,
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
        },
        iconWrapper: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: colors.iconColor,
            fontSize: "1.25rem",
        },
        content: {
            flex: 1,
            minWidth: 0,
        },
        title: {
            fontWeight: 600,
            marginBottom: 4,
        },
    };
});

export const CustomAlert = ({ severity, title, onClose, className, style, children }: CustomAlertProps) => {
    const { t } = useTranslation(["common"]);
    const theme = useTheme();
    const { classes } = useStyles({ severity });
    const colors = getAlertColors(theme, severity);

    return (
        <Box role="alert" className={clsx(classes.root, className)} style={style}>
            <Box className={classes.iconWrapper} aria-hidden sx={{ color: colors.iconColor }}>
                <i className={`fa-solid ${severityToIcon[severity]}`} />
            </Box>
            <Box className={classes.content}>
                {title ? (
                    <Typography variant="body1" className={classes.title} component="div">
                        {title}
                    </Typography>
                ) : null}
                <Box component="div" sx={{ fontSize: 14, lineHeight: 1.5, color: "text.primary" }}>
                    {children}
                </Box>
            </Box>
            {onClose ? (
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label={t("common:button.close")}
                    sx={{ flexShrink: 0, marginTop: -0.5, marginRight: -0.5 }}
                >
                    <i className="fa-solid fa-xmark" />
                </IconButton>
            ) : null}
        </Box>
    );
};
